/**
 * Heavy `POLYLINE` / `ACAD_TABLE` / `OLE2FRAME` block children × a dynamic-block
 * action.
 *
 * ## What this fixes — GitHub Codex review PR #785, thread 3997328676 (P2)
 *
 * `blockChildEntitySchema` (`drawing-model/model.ts`) admits a heavy
 * `Polyline`, a `Table` and an `OLE Frame` as block-definition children — the
 * BLOCKS section has the identical structure to ENTITIES per the Autodesk DXF
 * reference, and this repo's own `apps/web/public/abi1-test.dxf` carries eleven
 * heavy POLYLINEs inside block definitions. But every dispatcher in
 * `geometry.ts` (`translateEntity`, `stretchEntity`, `rotateEntity`,
 * `scaleEntity`, `mirrorEntity`) enumerated only the Tier-1 kinds and returned
 * the rest UNCHANGED from its `default:` arm. So a dynamic block whose Move
 * action selected a `Table` child left that table at the authored spot while
 * every sibling moved, and an Array action stacked every copy of such a child
 * on top of the original.
 *
 * ## Why this module DELEGATES instead of adding the arms here
 *
 * The transforms for exactly these three kinds already exist, ONCE, in
 * `command-engine`'s {@link import("@dxf-ai-editor/command-engine").transformEntityCoords}
 * — including the details a second implementation would have got wrong:
 *
 *   - the `Table` arm maps its insertion point through `transformPoint3D`, NOT
 *     the 2D helper, so DXF group 30 (the elevation) is not dropped, and it
 *     turns the group-11/21/31 HORIZONTAL-DIRECTION vector, which is the
 *     table's only orientation field (AcDbTable has no rotation scalar);
 *   - the `OLE Frame` arm folds all FOUR rectangle corners (the two stored ones
 *     are a DIAGONAL, whose image misses the extremes the other two reach) and
 *     carries z per corner;
 *   - the heavy-`Polyline` arm negates a vertex BULGE exactly when the map
 *     reverses orientation, maps a group-50 CURVE-FIT TANGENT through the
 *     linear part, and re-derives the OCS plane for a non-default group-210
 *     extrusion.
 *
 * Restating any of that here would be a second truth for the same DXF
 * semantics — the clone `npm run dup:check` exists to stop. So this module is
 * an ADAPTER: it builds the `TransformFn` each dynamic-block action implies and
 * hands the child to the one implementation.
 *
 * ## Dependency-graph decision (CLAUDE.md ПРИОРИТЕТ #2)
 *
 * `packages/block-evaluator` previously depended only on
 * `@dxf-ai-editor/drawing-model`. Adding `@dxf-ai-editor/command-engine`
 * introduces NO cycle: `command-engine` depends on `cad-core`, `contracts` and
 * `drawing-model`, none of which imports `block-evaluator` (and
 * `.dependency-cruiser.cjs`'s `cad-core-no-back-edges` rule structurally
 * forbids the one edge — `cad-core` → `block-evaluator` — that could close the
 * loop). Verified empirically, repo-wide, with the edge in place:
 * `npx depcruise apps packages scripts` reported 0 ERRORS (`no-circular` is
 * severity `error`). `block-evaluator`'s only consumer is `apps/web`, which
 * already bundles `command-engine`, so the new edge adds nothing to any bundle.
 *
 * ## Where a transform is REFUSED rather than applied
 *
 * Two of the three kinds cannot express some of the maps a dynamic-block action
 * can produce (a `Table` for two independent reasons — a resize and a mirror). The project's established answer is a VISIBLE refusal that
 * leaves the entity byte-identical, never a silent no-op (CLAUDE.md
 * ПРИОРИТЕТ #1), and the predicates that decide it are `command-engine`'s
 * — reused here so the evaluator and the in-place edit commands can never
 * disagree about what is representable:
 *
 *   - `Table` × a genuine RESIZE — {@link import("@dxf-ai-editor/command-engine").isTableScaleRefused}.
 *     A table's `rowHeights` (141) / `columnWidths` (142) are read-only
 *     projections of `contentGroups`, and its visible geometry is the
 *     pre-evaluated `*T<N>` graphics block at hard-coded unit scale, so a
 *     resize would desync the data from the picture. Only `|factor| !== 1` is
 *     declined; the `|factor| === 1` point reflection is exact and passes.
 *   - `Table` × an ORIENTATION-REVERSING map — {@link import("@dxf-ai-editor/command-engine").isTableMirrorRefused}.
 *     A table is placed by an insertion point plus a horizontal-direction
 *     VECTOR and carries no scale and no handedness field, so the image of a
 *     REFLECTION is a value it cannot name: mirroring `(1, 0)` gives `(-1, 0)`,
 *     which `tableAsBlockReference` re-places at unit scale as a 180° TURN.
 *     Read that module's header for the proof; the DOCUMENT half of this
 *     refusal already lands inside `transformEntityCoords`, and what this
 *     module adds is the ANNOUNCEMENT, so the evaluator's own sink stops
 *     reporting an empty `errors` for a decline the delegate already made.
 *   - `OLE Frame` × an OBLIQUE map — {@link import("@dxf-ai-editor/command-engine").isAxisAlignedFramePreservingMap}.
 *     An OLE2FRAME is two opposite WCS corners and nothing else, so a rectangle
 *     turned off the axes is not a value it can hold; answering with the
 *     axis-aligned BOX of the image would persist a DIFFERENT rectangle.
 *   - heavy `Polyline` × a PARTIAL crossing-frame STRETCH that also moves the
 *     entity's OCS PLANE — {@link stretchPolylineTableOleChild}'s own rule. See
 *     that function for the proof.
 *   - heavy `Polyline` THAT CARRIES WIDTH × a NON-SIMILARITY SCALE —
 *     {@link scalePolylineTableOleChild}'s own rule. A DXF width is one SCALAR
 *     per vertex (groups 40 / 41), and the image of a constant-width band under
 *     an anisotropic map is not a band of any single width, so there is no
 *     value the entity could hold. See that function for why this module
 *     REFUSES where the INSERT render path carries the affine instead.
 *
 * A refusal is reported through the optional {@link PolylineTableOleRefusalSink}
 * the dispatchers thread in from `actions.ts`, which turns it into an
 * `EvaluateBlockResult.errors` entry — the evaluator's one existing channel for
 * "this action did not do what it said".
 */

import type {
  BlockChildEntity,
  DrawingEntity,
} from "@dxf-ai-editor/drawing-model";
// The ONE lift from a heavy polyline's stored records to the points it is DRAWN
// at — subtype gate, Arbitrary Axis basis, elevation and the signed-zero-exact
// default-extrusion short circuit all included. See
// {@link heavyPolylineDrawnVertexXY} for why a crossing frame must consult it.
import {
  heavyPolylineWcsResolver,
  type HeavyPolylineGeometrySource,
} from "@dxf-ai-editor/cad-core";
import {
  explodedBandWidthScale,
  isAxisAlignedFramePreservingMap,
  isPolylineTableOleChild,
  isTableMirrorRefused,
  isTableScaleRefused,
  scaleEntityRadii,
  transformEntityCoords,
} from "@dxf-ai-editor/command-engine";

import type { CrossingFrame, EntityLike, Vec2 } from "./geometryPoints";
import { pointInFrame, translatePoint } from "./geometryPoints";

/** The 2D planar point map every in-place edit is expressed as. */
type TransformFn = (p: Vec2) => Vec2;

/** Why a dynamic-block action declined one of the three heavy child kinds. */
export type PolylineTableOleRefusalReason =
  /** `Table` × a genuine resize — the `*T<N>` graphics cannot follow. */
  | "table-resize"
  /** `OLE Frame` × a map its two stored corners cannot express. */
  | "ole-frame-oblique"
  /**
   * `Table` × an ORIENTATION-REVERSING map — a reflection its insertion point
   * and group-11/21/31 horizontal direction can only misname as a 180° TURN.
   */
  | "table-mirror"
  /** heavy `Polyline` × a partial STRETCH that also moves its OCS plane. */
  | "polyline-partial-stretch-moves-ocs-plane"
  /** heavy `Polyline` that CARRIES WIDTH × a non-similarity SCALE. */
  | "polyline-nonuniform-width-scale";

export interface PolylineTableOleRefusal {
  /** The block child's own id when it carries one (the evaluator's children do). */
  readonly entityId?: string;
  /** The DXF kind, so a caller can phrase the message without re-reading the entity. */
  readonly entityType: string;
  readonly reason: PolylineTableOleRefusalReason;
}

/**
 * Where a declined transform is reported. Optional at every call site: the
 * dispatchers stay usable without one (tests, tools), and a refusal then simply
 * leaves the entity unchanged — the same outcome, minus the report.
 */
export type PolylineTableOleRefusalSink = (
  refusal: PolylineTableOleRefusal,
) => void;

/**
 * `true` for the three kinds this module owns.
 *
 * Reuses `command-engine`'s {@link isPolylineTableOleChild} — the SAME
 * narrowing the two EXPLODE arms use — rather than restating the three type
 * literals, so "which kinds are these?" is answered in one place. The cast is
 * the duck-typing boundary this package is built on (`EntityLike` is
 * structurally wider than `BlockChildEntity`); the predicate reads only
 * `.type`, which `EntityLike` guarantees.
 */
export function isPolylineTableOleEntityLike(entity: EntityLike): boolean {
  return isPolylineTableOleChild(entity as unknown as BlockChildEntity);
}

function refusalOf(
  entity: EntityLike,
  reason: PolylineTableOleRefusalReason,
): PolylineTableOleRefusal {
  const id = (entity as { id?: unknown }).id;
  return {
    ...(typeof id === "string" ? { entityId: id } : {}),
    entityType: entity.type,
    reason,
  };
}

/**
 * Hand `entity` to `command-engine`'s one transform truth.
 *
 * The double cast is the package boundary: `transformEntityCoords` is exhaustive
 * over the typed `DrawingEntity` union while this package works duck-typed, and
 * every arm it can reach here spreads `...entity`, so fields the typed union does
 * not name (the evaluator's own `id`) ride through untouched.
 */
function delegate<T extends EntityLike>(entity: T, fn: TransformFn): T {
  return transformEntityCoords(
    entity as unknown as DrawingEntity,
    fn,
  ) as unknown as T;
}

/**
 * Apply the AFFINE map `fn` — the whole-entity map a MOVE / ROTATE / MIRROR
 * dynamic-block action implies — to one of the three heavy child kinds.
 *
 * `fn` MUST be affine: the delegate probes its linear part at `(0,0)`, `(1,0)`
 * and `(0,1)` to decide bulge handedness, tangent bearings and the table's
 * direction vector. Every caller in `geometry.ts` satisfies that (`translatePoint`,
 * `rotatePoint`, `scalePoint`, `reflectPoint` are each affine); the one
 * NON-affine action — a crossing-frame STRETCH, which moves only the vertices
 * inside the frame — has its own entry point, {@link stretchPolylineTableOleChild}.
 *
 * TWO of the three kinds decline some affine here, each at the SAME predicate
 * the in-place ROTATE / MIRROR commands use:
 *
 *   - `OLE Frame` × an oblique `fn` — declined rather than boxed;
 *   - `Table` × an ORIENTATION-REVERSING `fn` — declined rather than TURNED.
 *
 * The `Table` guard is this function's own only in WHERE IT REPORTS. The
 * delegate ALREADY declines the same map — `transformEntityCoords`' `Table` arm
 * consults the identical {@link isTableMirrorRefused} leaf and returns the SAME
 * object — so without this the evaluator inherited a correct no-op with an
 * EMPTY `errors`: a user flipped a block containing a table, the table stayed
 * put, and nothing said why (CLAUDE.md ПРИОРИТЕТ #1 forbids exactly that). The
 * condition is therefore IMPORTED, never restated: re-testing the determinant
 * sign here would be a second statement of "is this map orientation-reversing"
 * that could drift from the one the delegate acts on — the same reason the
 * `OLE Frame` line above asks `isAxisAlignedFramePreservingMap` rather than
 * re-deriving axis alignment, and the same reason the SCALE entry point below
 * asks `isTableScaleRefused` rather than re-spelling `|factor| !== 1`.
 *
 * WHICH DISPATCHERS REACH THIS. Only a Flip and a single-axis Scale can hand a
 * table a reflecting map. `translateEntity` and the Array expansions are pure
 * translations (linear part = identity, `det = +1`); `stretchEntity` builds its
 * `fn` from the same `translatePoint`; `rotateEntity` and the polar-stretch
 * rotation are rotations (`det = +1`). `mirrorEntity` is `det = -1` always, and
 * `scaleEntity` reaches `det = sx·sy < 0` when exactly one axis is negative —
 * the `x-only` / `y-only` scale types with a negative factor, which slip past
 * {@link isTableScaleRefused} because both `|sx|` and `|sy|` are then 1. Both
 * of those dispatchers already thread a sink, so putting the guard in this ONE
 * shared funnel announces both without a per-dispatcher restatement.
 *
 * The `Table` RESIZE refusal is a DIFFERENT and independent rule that belongs
 * to {@link scalePolylineTableOleChild}, which knows the scale factors as
 * scalars.
 */
export function transformPolylineTableOleChild<T extends EntityLike>(
  entity: T,
  fn: TransformFn,
  refusals?: PolylineTableOleRefusalSink,
): T {
  if (entity.type === "OLE Frame" && !isAxisAlignedFramePreservingMap(fn)) {
    refusals?.(refusalOf(entity, "ole-frame-oblique"));
    return entity;
  }
  if (isTableMirrorRefused(entity as unknown as DrawingEntity, fn)) {
    refusals?.(refusalOf(entity, "table-mirror"));
    return entity;
  }
  return delegate(entity, fn);
}

/**
 * The four DXF WIDTH groups a heavy polyline's band is built from: the
 * entity-level 40 / 41 defaults and the per-vertex 40 / 41 overrides. Exactly
 * the set `scaleEntityRadii`'s `Polyline` arm walks — read here only to answer
 * "is there a band at all?", never to write one.
 */
interface PolylineBandWidths {
  readonly defaultStartWidth?: number | undefined;
  readonly defaultEndWidth?: number | undefined;
  readonly vertices: ReadonlyArray<{
    readonly startWidth?: number | undefined;
    readonly endWidth?: number | undefined;
  }>;
}

/**
 * `true` when this polyline is DRAWN AS A BAND — some width group is present
 * and non-zero.
 *
 * An ABSENT per-vertex 40/41 inherits the entity default (`scaleEntityRadii`'s
 * "PRESENCE BITS" note), so a vertex that carries neither is covered by the two
 * defaults; an explicit `0` really is zero width. A polyline with no width
 * anywhere is a pure CENTRELINE — a point set — and every affine maps it
 * exactly, which is why the refusal below is gated on this rather than on the
 * scale factors alone. Every heavy POLYLINE in this repo's own
 * `apps/web/public/abi1-test.dxf` is zero-width, so the common path is
 * untouched.
 */
function carriesBandWidth(entity: EntityLike): boolean {
  const e = entity as unknown as PolylineBandWidths;
  const wide = (w?: number): boolean => w !== undefined && w !== 0;
  if (wide(e.defaultStartWidth) || wide(e.defaultEndWidth)) return true;
  return e.vertices.some((v) => wide(v.startWidth) || wide(v.endWidth));
}

/**
 * `true` when scaling by `(sx, sy)` would leave this polyline's band at a width
 * that is NOT the image of the authored one — the state Codex round 15 names:
 * "persists the transformed centerline with the original scalar widths".
 *
 * ## Why a refusal here and NOT in the INSERT render path
 *
 * A DXF width is ONE SCALAR per vertex, so the image of a constant-width band
 * under an anisotropic map — a horizontal width-2 band y-scaled by 3 is six
 * units tall — is not a value any group 40 / 41 can hold. That is a
 * representational limit, not a missing effort, and the two paths answer it
 * differently BECAUSE THEY WRITE DIFFERENT THINGS:
 *
 *   - the INSERT render path keeps the AUTHORED width and stamps a BAND FRAME
 *     on the renderable (`scene3d/adapters/typed/blockChildBandFrame.ts`), so
 *     the omitted affine is applied downstream when the band loops are built.
 *     Nothing is lost, because a renderable is free to carry a frame;
 *   - this evaluator answers with an ENTITY. Its result feeds the render path,
 *     EXPLODE (which writes a persisted DXF POLYLINE) and the property
 *     palette, and only the first of those three could hold a frame. Inventing
 *     one here would therefore be exact on one consumer and silently lossy on
 *     the other two — a second representation of the same geometry, which is
 *     what CLAUDE.md ПРИОРИТЕТ #2.1 forbids and what the width scalar's own
 *     one-truth choice ({@link explodedBandWidthScale}) exists to prevent.
 *
 * So the entity is returned BYTE-IDENTICAL and the action says so, bilingually
 * — a visible refusal, never a silent width-2 render (ПРИОРИТЕТ #1). AutoCAD
 * draws the same boundary on the persisted side: its own SCALE command takes a
 * single uniform factor and offers no anisotropic option at all.
 *
 * ## The similarity test is DERIVED from the one truth, not restated
 *
 * {@link explodedBandWidthScale} already decides this: it answers `|sx|` when
 * and only when the map is a similarity, and `1` otherwise. So the factor it
 * returns equals BOTH axis magnitudes exactly when the band survives — and
 * asking it that way rather than re-spelling `Math.abs(sx) === Math.abs(sy)`
 * means the two can never drift. (Re-spelling it as `factor === Math.abs(sx)`
 * alone would be WRONG: `S(1, 3)` returns `1`, which is `|sx|`.)
 */
function nonUniformScaleLosesBand(
  entity: EntityLike,
  sx: number,
  sy: number,
): boolean {
  const factor = explodedBandWidthScale(sx, sy);
  const bandSurvives = factor === Math.abs(sx) && factor === Math.abs(sy);
  return !bandSurvives && carriesBandWidth(entity);
}

/**
 * The SCALE action's entry point — the one case that needs the factors as
 * SCALARS rather than as an opaque `fn`.
 *
 *   - `Table`: declined at a genuine resize, per
 *     {@link isTableScaleRefused}. That predicate takes ONE scalar because
 *     `geometry/scale-entities` applies one; a dynamic-block Scale action has an
 *     independent `sx` / `sy`, so BOTH axes are put to it — the table is
 *     size-preserving only when each is `±1`. That admits three maps, and they
 *     do NOT all survive: `(-1, -1)` is the point reflection, which the `Table`
 *     arm maps exactly through its horizontal-direction vector, while the two
 *     AXIS MIRRORS `(-1, 1)` and `(1, -1)` reverse orientation and are declined
 *     — one step further on, by {@link transformPolylineTableOleChild}'s
 *     {@link isTableMirrorRefused} guard, which is where the Flip action's
 *     identical refusal is announced too.
 *   - heavy `Polyline`: the positions go through the delegate, and the WIDTH
 *     fields (groups 40 / 41, entity-level `defaultStartWidth` /
 *     `defaultEndWidth` and per-vertex `startWidth` / `endWidth`) go through
 *     `command-engine`'s {@link scaleEntityRadii}, whose `Polyline` case is the
 *     one truth for them. The scalar those widths take is
 *     {@link explodedBandWidthScale} — `|sx|` under a similarity, `1`
 *     otherwise. Reusing that function rather than guessing a scalar here is
 *     the point: the EXPLODE path already makes the identical choice.
 *
 *     A polyline that CARRIES WIDTH is DECLINED under a non-similarity instead
 *     of taking that `1` — see {@link nonUniformScaleLosesBand}.
 *   - `OLE Frame`: its SIZE is its two stored corners, so the delegate's
 *     four-corner fold already scales it exactly; `scaleEntityRadii` has no
 *     `OLE Frame` case and returns it untouched.
 */
export function scalePolylineTableOleChild<T extends EntityLike>(
  entity: T,
  fn: TransformFn,
  sx: number,
  sy: number,
  refusals?: PolylineTableOleRefusalSink,
): T {
  if (entity.type === "Polyline" && nonUniformScaleLosesBand(entity, sx, sy)) {
    refusals?.(refusalOf(entity, "polyline-nonuniform-width-scale"));
    return entity;
  }
  if (
    entity.type === "Table" &&
    (isTableScaleRefused(entity as unknown as DrawingEntity, sx) ||
      isTableScaleRefused(entity as unknown as DrawingEntity, sy))
  ) {
    refusals?.(refusalOf(entity, "table-resize"));
    return entity;
  }
  const moved = transformPolylineTableOleChild(entity, fn, refusals);
  // Reference identity means the delegate DECLINED (it returns the SAME object
  // on a refusal, and builds a new one otherwise). A declined entity must stay
  // byte-identical, so its size fields must not be scaled either. This is
  // REACHABLE, not merely defensive: an `x-only` / `y-only` Scale with a
  // negative factor is a MIRROR (`det = sx·sy < 0`) that clears the
  // `isTableScaleRefused` gate above — both magnitudes are 1 — and is then
  // declined as `"table-mirror"` inside the call on the line before this one.
  if (moved === entity) return entity;
  return scaleEntityRadii(
    moved as unknown as DrawingEntity,
    { x: 0, y: 0 },
    explodedBandWidthScale(sx, sy),
  ) as unknown as T;
}

/** The subset of a heavy POLYLINE this module reads directly. */
interface PolylineShape {
  readonly vertices: ReadonlyArray<{
    readonly location: { readonly x: number; readonly y: number };
  }>;
  readonly extrusion?: {
    readonly x: number;
    readonly y: number;
    readonly z: number;
  };
  readonly elevation?: { readonly z: number };
}

/**
 * The XY each heavy-POLYLINE vertex is SEEN at, in the block's own coordinates
 * — the frame a crossing window is drawn in.
 *
 * ## The defect this closes (Codex round 15, P2)
 *
 * A 2D heavy POLYLINE with a non-default group-210 extrusion stores its
 * vertices in OCS, and the Arbitrary Axis Algorithm can put those numbers
 * nowhere near where the grip appears: under extrusion `(0, 0, -1)` the OCS x
 * axis is WCS −X, so a vertex STORED at `(5, 0)` is DRAWN at `(-5, 0)`. A
 * crossing frame is authored against the DRAWING, so classifying it against the
 * stored numbers meant a frame around the visible grip caught nothing while a
 * frame around empty space at `(+5, 0)` moved it — a user dragging a grip they
 * can see and having nothing happen.
 *
 * The DELTA was never wrong: it travels through `transformEntityCoords`, whose
 * `Polyline` arm re-derives the plane through `ocsPlanarTransform`. Only the
 * CLASSIFICATION read raw OCS.
 *
 * ## One truth, not a second Arbitrary Axis
 *
 * `cad-core`'s {@link heavyPolylineWcsResolver} is the lift the renderer, the
 * picking path and `wcsBoundsLeaf`'s `heavyPolylineLocal` all use. It owns the
 * whole rule — the group-70 SUBTYPE gate (a 3D polyline / mesh is ALREADY WCS
 * and must not be tilted), the elevation along the normal, the
 * signed-zero-exact short circuit for the default extrusion, and the
 * out-of-range guard — so this module asks it rather than deriving the basis a
 * second time (CLAUDE.md ПРИОРИТЕТ #2.1).
 *
 * Exported because the POLAR STRETCH dispatcher's `entityFullyInsideFrame`
 * (`actions.polarStretch.ts`) classifies the SAME vertices against the SAME
 * kind of frame and had the identical defect.
 */
export function heavyPolylineDrawnVertexXY(entity: EntityLike): Vec2[] {
  const source = entity as unknown as HeavyPolylineGeometrySource;
  const resolve = heavyPolylineWcsResolver(source);
  return source.vertices.map((_v, i) => {
    const p = resolve(i);
    return { x: p.x, y: p.y };
  });
}

/**
 * `true` when the delegate re-derived the polyline's OCS PLANE — its group-210
 * extrusion or its elevation came back different.
 *
 * A 2D heavy POLYLINE with a NON-DEFAULT extrusion stores its vertices in OCS,
 * and `ocsPlanarTransform` (the delegate's OCS path) answers a transform with a
 * NEW plane plus every vertex re-expressed in it — including a new elevation,
 * which for a translation is the source plane shifted along its own normal.
 */
function ocsPlaneMoved(before: PolylineShape, after: PolylineShape): boolean {
  const b = before.extrusion;
  const a = after.extrusion;
  if ((b === undefined) !== (a === undefined)) return true;
  if (b !== undefined && a !== undefined) {
    if (b.x !== a.x || b.y !== a.y || b.z !== a.z) return true;
  }
  return before.elevation?.z !== after.elevation?.z;
}

/**
 * AutoCAD-style STRETCH for the three heavy child kinds — the ONE action whose
 * map is not affine, because only the grips inside the crossing frame move.
 *
 * ## `Table` and `OLE Frame` move as a UNIT
 *
 * AutoCAD's STRETCH stretches what a crossing window partially encloses and
 * MOVES what it cannot stretch. Neither of these two can be stretched:
 *
 *   - a `Table`'s size lives in `contentGroups` and in the pre-evaluated
 *     `*T<N>` graphics block, which is exactly why `isTableScaleRefused`
 *     declines a resize — so its only stretchable answer is the whole-unit move
 *     the `Block` case already makes on its insertion point;
 *   - an `OLE Frame`'s two stored corners are its grips, but dragging ONE of
 *     them resizes the embedded picture, the same "persist a rectangle the user
 *     never authored" defect `oleFrameTransformRefusal.ts` was written to stop.
 *     So the frame moves whole when EITHER corner is caught — the same
 *     EITHER-grip rule `geometry.ts` applies to a justified Text's
 *     insertion/alignment pair.
 *
 * ## The heavy `Polyline` stretches PER VERTEX
 *
 * Like the `LWPOLYLINE` case it is the heavy twin of: each VERTEX whose DRAWN
 * position lies inside the frame translates by `delta`, the rest stay. "Drawn",
 * not "stored": a non-default group-210 extrusion puts the stored numbers
 * somewhere else entirely, so the frame is answered through
 * {@link heavyPolylineDrawnVertexXY}.
 *
 * The moved records are taken from the delegate's FULL translation rather than
 * written here, so the per-vertex rules (z carried, bulge untouched — a
 * translation preserves orientation — group-50 tangent untouched, because the
 * linear part is the identity and the delegate's no-op snap leaves the stored
 * bytes alone) come from the one implementation instead of being re-derived.
 *
 * REFUSAL — a partial stretch that also MOVES THE PLANE. A 2D heavy polyline
 * with a non-default group-210 extrusion has all of its vertices in ONE plane
 * BY CONSTRUCTION, and the delegate answers a translation of such a polyline
 * with a new elevation (the plane shifted along its own normal) plus every
 * vertex re-expressed in it. Splicing a MOVED subset from that result onto a
 * RESTING subset from the source would put the two halves in two different
 * planes — a polyline that no longer exists. So a partial stretch of a
 * plane-moving polyline is DECLINED, visibly, and the entity is returned
 * byte-identical. The ALL-in-frame and NONE-in-frame stretches of the same
 * polyline are unaffected: the first is the delegate's own whole-entity
 * translation and the second is a no-op.
 */
export function stretchPolylineTableOleChild<T extends EntityLike>(
  entity: T,
  frame: CrossingFrame,
  delta: Vec2,
  refusals?: PolylineTableOleRefusalSink,
): T {
  const fn: TransformFn = (p) => translatePoint(p, delta);

  if (entity.type === "Table") {
    const e = entity as unknown as { insertionPoint: Vec2 };
    return pointInFrame(e.insertionPoint, frame)
      ? transformPolylineTableOleChild(entity, fn, refusals)
      : entity;
  }

  if (entity.type === "OLE Frame") {
    const e = entity as unknown as { upperLeft: Vec2; lowerRight: Vec2 };
    return pointInFrame(e.upperLeft, frame) || pointInFrame(e.lowerRight, frame)
      ? transformPolylineTableOleChild(entity, fn, refusals)
      : entity;
  }

  const source = entity as unknown as PolylineShape;
  // Against the points the vertices are DRAWN at, not the stored OCS numbers —
  // see {@link heavyPolylineDrawnVertexXY}. One entry per record, in order, so
  // the indices still line up with `source.vertices` for the splice below.
  const caught = heavyPolylineDrawnVertexXY(entity).map((p) =>
    pointInFrame(p, frame),
  );
  const movedCount = caught.reduce((n, hit) => (hit ? n + 1 : n), 0);
  if (movedCount === 0) return entity;

  const full = transformPolylineTableOleChild(entity, fn, refusals);
  if (movedCount === source.vertices.length) return full;

  const image = full as unknown as PolylineShape;
  if (ocsPlaneMoved(source, image)) {
    refusals?.(refusalOf(entity, "polyline-partial-stretch-moves-ocs-plane"));
    return entity;
  }
  // Start from the AUTHORED records and swap in the delegate's image for the
  // caught vertices only. `?? v` can never fire (the delegate's `Polyline` arm
  // maps `vertices` one-for-one) and is there so the expression needs no
  // non-null assertion under `noUncheckedIndexedAccess`.
  return {
    ...full,
    vertices: source.vertices.map((v, i) =>
      caught[i] ? (image.vertices[i] ?? v) : v,
    ),
  };
}
